/**
 * §7.7 preferences. Action-required email cannot be switched off: those are
 * re-auth prompts, rental approvals, consent requests and payment failures — the
 * messages whose whole purpose is to reach someone who is not looking at the app.
 */

import { useEffect, useState } from 'react';
import { useApi, useMutation } from '../../hooks/useApi';
import type { NotificationPreferences } from '../../api/types';
import { Card, Field, LiveRegion, Loading } from '../../components/ui';
import { ErrorView, InlineError } from '../../components/ErrorView';
import { routes } from '../../api/routes';

export function NotificationPreferencesRoute() {
  const prefs = useApi<NotificationPreferences>(routes.me.notificationPreferences);
  const save = useMutation<Record<string, unknown>, unknown>(routes.me.notificationPreferences, {
    method: 'PATCH',
  });
  const [form, setForm] = useState<NotificationPreferences | null>(null);

  useEffect(() => {
    if (prefs.data) setForm(prefs.data);
  }, [prefs.data]);

  if (prefs.initialLoading) return <Loading label="Loading preferences" />;
  if (prefs.problem) return <ErrorView problem={prefs.problem} onRetry={prefs.reload} />;
  if (!form) return null;

  return (
    <div className="narrow">
      <Card title="Notifications">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void save.run({ ...form });
          }}
        >
          <div className="field">
            <label htmlFor="np-action">
              <input
                id="np-action"
                type="checkbox"
                checked
                disabled
                readOnly
              />{' '}
              Email me when something needs action
            </label>
            <p className="field-hint">
              Always on. These are re-auth prompts, rental requests, consent requests and payment
              failures — they have to reach you when the app is closed.
            </p>
          </div>

          <div className="field">
            <label htmlFor="np-info">
              <input
                id="np-info"
                type="checkbox"
                checked={form.email_informational}
                onChange={(e) => setForm({ ...form, email_informational: e.target.checked })}
              />{' '}
              Email me informational updates
            </label>
          </div>

          <div className="field">
            <label htmlFor="np-inapp">
              <input
                id="np-inapp"
                type="checkbox"
                checked={form.in_app_informational}
                onChange={(e) => setForm({ ...form, in_app_informational: e.target.checked })}
              />{' '}
              Show informational updates in the notification centre
            </label>
          </div>

          <Field id="np-digest" label="Digest">
            <select
              id="np-digest"
              className="select"
              value={form.email_digest}
              onChange={(e) =>
                setForm({ ...form, email_digest: e.target.value as NotificationPreferences['email_digest'] })
              }
            >
              <option value="off">Off</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </Field>

          {save.problem ? <InlineError problem={save.problem} /> : null}
          <button type="submit" className="btn btn-primary" disabled={save.pending}>
            {save.pending ? 'Saving…' : 'Save'}
          </button>
          {save.data ? <LiveRegion>Saved.</LiveRegion> : null}
        </form>
      </Card>
    </div>
  );
}
